import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Link2, Copy, Users, CalendarCheck, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BadgePill } from "@/components/Brand";
import { toast } from "sonner";

export default function HostManage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [study, setStudy] = useState(null);
  const [members, setMembers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [invite, setInvite] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, m, a] = await Promise.all([
        api.get(`/studies/${id}`),
        api.get(`/studies/${id}/members`),
        api.get(`/studies/${id}/attendance`),
      ]);
      setStudy(s.data); setMembers(m.data.members); setAttendance(a.data.attendance);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); nav(`/study/${id}`); }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const genInvite = async () => {
    try {
      const { data } = await api.post(`/studies/${id}/invite`);
      const url = `${window.location.origin}/invite/${data.token}`;
      setInvite(url);
      navigator.clipboard?.writeText(url);
      toast.success("Invite link copied");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendMsg = async () => {
    if (!msg.trim()) return;
    try { await api.post(`/studies/${id}/messages`, { text: msg }); setMsg(""); toast.success("Sent to members"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!study) return <div className="mx-auto max-w-4xl px-4 py-16 text-center text-[#6B6763]">Loading…</div>;

  return (
    <div className="mx-auto max-w-4xl px-4 py-5 sm:px-6">
      <button onClick={() => nav(`/study/${id}`)} data-testid="back-to-study" className="mb-3 flex items-center gap-1 text-sm font-medium text-[#6B6763] hover:text-[#0F3D66]">
        <ChevronLeft size={16} /> Back to study
      </button>
      <div className="mb-1 flex items-center gap-2">
        <h1 className="font-display text-2xl font-bold tracking-tight text-[#0F3D66]">Host Toolkit</h1>
        {study.active ? <BadgePill tone="sage">Active</BadgePill> : <BadgePill tone="warn">Needs members</BadgePill>}
      </div>
      <p className="text-sm text-[#6B6763]">{study.title}</p>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {/* Members */}
        <Panel title="Members" icon={Users} testid="host-members">
          <div className="space-y-2">
            {members.map((m) => (
              <div key={m.user_id} className="flex items-center justify-between text-sm">
                <span className="text-[#0F3D66]">{m.name}</span>
                <BadgePill tone={m.role === "host" ? "primary" : "muted"}>{m.role}</BadgePill>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-[#6B6763]">{study.member_count} confirmed · minimum 3 to stay active.</p>
        </Panel>

        {/* Invite */}
        <Panel title="Invite close friends" icon={Link2} testid="host-invite">
          <p className="text-sm text-[#6B6763]">Share a private link so friends can join directly.</p>
          <Button onClick={genInvite} data-testid="generate-invite-btn" className="mt-3 rounded-full bg-[#748C70] text-white hover:bg-[#5f7259]"><Link2 size={15} className="mr-1.5" /> Generate invite link</Button>
          {invite && (
            <div className="mt-3 flex items-center gap-2 rounded-xl bg-[#F3EEE7] p-2.5 text-xs">
              <span className="truncate text-[#0F3D66]" data-testid="invite-link">{invite}</span>
              <button onClick={() => { navigator.clipboard?.writeText(invite); toast.success("Copied"); }} className="shrink-0 text-[#F36353]"><Copy size={14} /></button>
            </div>
          )}
        </Panel>

        {/* Message members */}
        <Panel title="Message members" icon={Send} testid="host-message">
          <Textarea data-testid="host-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Send an update to everyone…" className="rounded-xl border-[#E8E4DF]" />
          <Button onClick={sendMsg} data-testid="host-message-send" className="mt-2 rounded-full bg-[#F36353] text-white hover:bg-[#D94A3D]"><Send size={15} className="mr-1.5" /> Send</Button>
        </Panel>

        {/* Attendance */}
        <Panel title="Attendance" icon={CalendarCheck} testid="host-attendance">
          {attendance.length === 0 && <p className="text-sm text-[#6B6763]">No check-ins yet. Members check in at the venue during the meeting window.</p>}
          <div className="space-y-3">
            {attendance.map((a) => (
              <div key={a.session_date}>
                <div className="text-sm font-semibold text-[#0F3D66]">{a.session_date}</div>
                <div className="text-xs text-[#6B6763]">{a.attendees.join(", ")} ({a.attendees.length})</div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function Panel({ title, icon: Icon, testid, children }) {
  return (
    <div className="rounded-2xl border border-[#E8E4DF] bg-white p-5" data-testid={testid}>
      <div className="mb-3 flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-full bg-[#F36353]/12 text-[#F36353]"><Icon size={16} /></span>
        <h2 className="font-display text-base font-semibold text-[#0F3D66]">{title}</h2>
      </div>
      {children}
    </div>
  );
}
